import { SmallApprovedIcon, TileRejectedIcon as SmallRejectedIcon } from 'Icons'

import ApprovalAnimation from './ApprovalAnimation'
import ReplyForm from './conversation/ReplyForm'

export interface PostActionProps {
  postId: string
  isLoading: boolean
  postStatus?: string
  isMobile?: boolean
  localPostStatus?: string
  reviewKey: string
  className?: string
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  // Reply functionality props
  canReply?: boolean
  isReplied?: boolean
  isSubmitting?: boolean
  onReplySubmit?: (text: string, postId: string, commentId: string) => void
  lastMessageId?: string
  isConversationsEnabled?: boolean
  // Approval animation props
  showApprovalAnimation?: boolean
  onApprovalAnimationComplete?: () => void
}

const PostAction = (props: PostActionProps) => {
  const {
    postId,
    isLoading,
    postStatus,
    isMobile,
    localPostStatus,
    reviewKey,
    updatePostState,
    className,
    // Reply functionality props
    canReply,
    isReplied,
    isSubmitting: replySubmitting,
    onReplySubmit,
    lastMessageId,
    // Approval animation props
    showApprovalAnimation,
    onApprovalAnimationComplete,
  } = props
  if (!postStatus) return null

  const handleUndo = () => {
    updatePostState(reviewKey, postId, 'pending')
  }

  const handleApproved = () => {
    updatePostState(reviewKey, postId, 'approved')
  }

  const handleRequestChange = () => {
    updatePostState(reviewKey, postId, 'requestChange')
  }

  const isApproved = postStatus === 'approved'
  const isRequestChange = postStatus === 'requestChange'

  const showReplyForm =
    isRequestChange &&
    canReply &&
    !isReplied &&
    onReplySubmit &&
    Boolean(lastMessageId)

  return (
    <div className={className}>
      {!isMobile && (
        <h4 className="text-[16px] font-bold mb-[8px]">
          Post History & Discussion
        </h4>
      )}

      <div
        className={`${isLoading ? 'cursor-wait opacity-70' : ''} font-[Inter] w-full  lg:max-w-[360px]`}
      >
        {showApprovalAnimation && onApprovalAnimationComplete && (
          <ApprovalAnimation
            onComplete={onApprovalAnimationComplete}
            onUndo={handleUndo}
          />
        )}

        {(isApproved || isRequestChange) && !showApprovalAnimation && (
          <div
            className={`
              flex items-center justify-between pr-[12px] relative whitespace-nowrap rounded-[8px] h-[56px]
              ${showReplyForm ? 'rounded-b-none' : ''} 
              w-full lg:max-w-[360px] 
              ${isApproved ? 'bg-[#E7F8F2]' : 'bg-[#fee5e1]'}
            `}
          >
            <div
              className={`
                rounded-[8px] rounded-r-none min-w-[56px] h-[56px] text-[40px] text-[#ffffff] flex items-center justify-center 
                ${isApproved ? 'bg-[#10B981]' : 'bg-[#FA4F38]'} 
                ${showReplyForm ? 'rounded-b-none' : ''}
              `}
            >
              {isApproved ? (
                <i className="fa-regular fa-check" />
              ) : (
                <i className="fa-regular fa-xmark" />
              )}
            </div>
            <div className={`text-center text-[16px] font-[600]`}>
              {isApproved ? 'Approved!' : 'Change requested!'}
            </div>
            <div
              className="text-[12px] text-[#4f31ff] cursor-pointer font-[500]"
              onClick={handleUndo}
            >
              Undo
            </div>
          </div>
        )}

        {showReplyForm && (
          <ReplyForm
            postId={postId}
            isSubmitting={replySubmitting || false}
            isMobile={isMobile}
            onSubmit={(text, commentId) =>
              onReplySubmit(text, postId, commentId)
            }
            lastMessageId={lastMessageId}
            autoFocus={!isMobile}
          />
        )}
      </div>

      {isMobile && localPostStatus !== 'pending' && (
        <div className="mb-[10px]">
          <hr className="border-none border-b border-[#dedafe] mx-auto my-[12px] mb-[16px]" />
          {postStatus === 'approved' && (
            <div>
              <div className="flex items-center justify-center">
                <div className="w-[28px] mr-[10px]">
                  <SmallApprovedIcon />
                </div>
                Post approved
              </div>
              <p className="text-center text-[12px] mt-[10px] mb-0">
                To undo this action, visit this link on a desktop device
              </p>
            </div>
          )}
          {postStatus === 'requestChange' && (
            <div>
              <div className="flex items-center justify-center">
                <div className="w-[28px] mr-[10px]">
                  <SmallRejectedIcon />
                </div>
                <div>Change requested</div>
              </div>
              <p className="text-center text-[12px] mt-[10px] mb-0">
                To undo this action, visit this link on a desktop device
              </p>
            </div>
          )}
        </div>
      )}

      <div
        className={`${isMobile && !(localPostStatus !== 'pending' && !isLoading) ? 'mb-[12px]' : ''}`}
      >
        {postStatus === 'pending' && (
          <div
            className={`
              h-[56px] w-full lg:max-w-[360px] flex cursor-pointer
              ${isMobile ? 'gap-[24px] justify-center' : 'gap-[16px]'}
              ${isLoading ? 'cursor-wait opacity-70' : ''}
            `}
          >
            <button
              data-testid="approve"
              className={`
                flex-1 border-none cursor-pointer rounded-[8px] h-full text-center uppercase grid place-items-center text-white
                font-[Inter] text-[16px] font-[600] leading-[24px] bg-[#10B981] text-[#ffffff]
                ${!isLoading ? 'hover:shadow-[0_4px_10px_rgba(102,185,19,0.4)]' : 'hover:shadow-none'}
              `}
              onClick={handleApproved}
            >
              approve
            </button>

            <button
              data-testid="requestChange"
              className={`
                flex-1 border-none cursor-pointer rounded-[8px] h-full text-center uppercase grid place-items-center text-white
                font-[Inter] text-[16px] font-[600] leading-[24px] bg-[#fa4f38] text-[#ffffff]
                ${!isLoading ? 'hover:shadow-[0_4px_10px_rgba(250,79,56,0.4)]' : 'hover:shadow-none'}
              `}
              onClick={handleRequestChange}
            >
              request change
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PostAction
